const GOOGLE_CHIRP_VOICES = [
  { value: "Aoede", label: "Aoede（柔らかめ）" },
  { value: "Kore", label: "Kore（落ち着き）" },
  { value: "Leda", label: "Leda（明るめ）" },
  { value: "Sulafat", label: "Sulafat（中庸）" },
  { value: "Charon", label: "Charon（低め）" },
  { value: "Fenrir", label: "Fenrir（太め）" },
];

const GOOGLE_GEMINI_VOICES = [
  { value: "Kore", label: "Kore（落ち着き）" },
  { value: "Aoede", label: "Aoede（柔らかめ）" },
  { value: "Leda", label: "Leda（明るめ）" },
  { value: "Callirrhoe", label: "Callirrhoe（抑えめ）" },
  { value: "Umbriel", label: "Umbriel（低め）" },
  { value: "Orus", label: "Orus（太め）" },
];

export function TtsSettingsPanel({
  isDraft,
  ttsProvider,
  ttsModel,
  ttsVoice,
  ttsInstructions,
  onProviderChange,
  onModelChange,
  onVoiceChange,
  onInstructionsChange,
}: {
  isDraft: boolean;
  ttsProvider: "openai" | "google";
  ttsModel: string;
  ttsVoice: string;
  ttsInstructions: string;
  onProviderChange: (provider: "openai" | "google") => void;
  onModelChange: (model: string) => void;
  onVoiceChange: (voice: string) => void;
  onInstructionsChange: (instructions: string) => void;
}) {
  return (
    <div className="p-3 bg-slate-50 rounded-lg space-y-3">
      <label className="block text-xs font-semibold text-slate-700">音声合成 (TTS)</label>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] text-slate-500 mb-0.5">プロバイダ</label>
          <select
            value={ttsProvider}
            onChange={(e) => {
              const provider = e.target.value as "openai" | "google";
              onProviderChange(provider);
              if (provider === "google") {
                onModelChange("chirp-3-hd");
                onVoiceChange("Kore");
              } else {
                onModelChange("tts-1");
                onVoiceChange("nova");
              }
            }}
            disabled={!isDraft}
            className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
          >
            <option value="openai">OpenAI</option>
            <option value="google">Google Cloud</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 mb-0.5">モデル</label>
          <select
            value={ttsModel}
            onChange={(e) => {
              const model = e.target.value;
              onModelChange(model);
              if (ttsProvider === "google") {
                onVoiceChange(model.startsWith("gemini-") ? "Kore" : "Aoede");
                return;
              }
              onVoiceChange(model === "gpt-4o-mini-tts" ? "coral" : "nova");
            }}
            disabled={!isDraft}
            className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
          >
            {ttsProvider === "google" ? (
              <>
                <option value="chirp-3-hd">chirp-3-hd（推奨・低遅延）</option>
                <option value="gemini-2.5-flash-tts">gemini-2.5-flash-tts（指示対応）</option>
                <option value="gemini-2.5-pro-tts">gemini-2.5-pro-tts（高品質・検証）</option>
              </>
            ) : (
              <>
                <option value="tts-1">tts-1（標準・低遅延）</option>
                <option value="tts-1-hd">tts-1-hd（高品質）</option>
                <option value="gpt-4o-mini-tts">gpt-4o-mini-tts（高品質・指示対応）</option>
              </>
            )}
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 mb-0.5">ボイス</label>
          <select
            value={ttsVoice}
            onChange={(e) => onVoiceChange(e.target.value)}
            disabled={!isDraft}
            className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
          >
            {ttsProvider === "google" ? (
              (ttsModel.startsWith("gemini-") ? GOOGLE_GEMINI_VOICES : GOOGLE_CHIRP_VOICES).map((voice) => (
                <option key={voice.value} value={voice.value}>{voice.label}</option>
              ))
            ) : ttsModel === "gpt-4o-mini-tts" ? (
              <>
                <option value="coral">coral（女性・自然）</option>
                <option value="alloy">alloy（中性）</option>
                <option value="ash">ash（男性）</option>
                <option value="sage">sage（女性・落ち着き）</option>
                <option value="ballad">ballad（男性・穏やか）</option>
                <option value="verse">verse（女性・明るい）</option>
              </>
            ) : (
              <>
                <option value="nova">nova（女性・明るい）</option>
                <option value="alloy">alloy（中性）</option>
                <option value="echo">echo（男性）</option>
                <option value="fable">fable（男性・英国風）</option>
                <option value="onyx">onyx（男性・低音）</option>
                <option value="shimmer">shimmer（女性・柔らか）</option>
              </>
            )}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] text-slate-500 mb-0.5">トーン指示（任意）</label>
        <textarea
          value={ttsInstructions}
          onChange={(e) => onInstructionsChange(e.target.value)}
          disabled={!isDraft}
          rows={2}
          className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
          placeholder="例: 落ち着いた日本語の電話営業トーンで、押し売り感を抑えて短く話す"
        />
      </div>
      <p className="text-[10px] text-slate-400">
        Google は Chirp 3 HD を本命、Gemini-TTS はトーン検証向けです。既存値がないキャンペーンは OpenAI 既定で動作します。
      </p>
    </div>
  );
}
